"use client";
import React from "react";
import { Modal } from "antd";
import { Star, ChevronRight } from "lucide-react";
import Link from "components/common/Link";
import Image from "components/common/Image";
import { get, map } from "utils/lodash";
import { formatPrice } from "utils/currencyFormatter";
import { formatDate } from "utils/dateFormatter";
import dayjs from "utils/day";
import { richTextToPlain } from "utils/lexicalFormatter";
import { offerDiscountPct } from "services/campaignPackage/formatter";
import helper from "utils/helper";
import { useT } from "providers/I18nProvider";
import { roundRating } from "components/home/Card/cardFormat";

interface OfferDetailModalProps {
  open: boolean;
  pkg: any;
  onClose: () => void;
  onBook: () => void;
}

const OfferDetailModal: React.FC<OfferDetailModalProps> = ({
  open,
  pkg,
  onClose,
  onBook,
}) => {
  const t = useT();
  if (!pkg) return null;

  const shop = get(pkg, "shop");
  const service = get(pkg, "service");
  const campaign = get(pkg, "campaign");
  const avatar = helper.imageUrl(get(pkg, "shop.avatar.url")) || "";
  // Ảnh ưu đãi: ưu tiên gallery của campaign (bản card cho nhẹ), fallback avatar shop
  const gallery = get(campaign, "gallery", []) || [];
  const coverRaw =
    get(gallery, "0.sizes.card.url") || get(gallery, "0.url") || "";
  const cover = coverRaw ? helper.imageUrl(coverRaw) : avatar;
  const coverAlt = get(gallery, "0.alt") || get(pkg, "name") || "";
  const rating = Number(get(shop, "rating")) || 0;
  // Dùng chung cách làm tròn với card để điểm card = điểm modal.
  const ratingText = roundRating(rating);
  const offerPrice = get(pkg, "displayDiscountPrice");
  const originalPrice = get(service, "menuPrice");
  const duration = get(service, "duration");
  const categories = map(
    get(pkg, "subShopCategories", []),
    (c) => get(c, "name"),
  ).filter(Boolean);

  const discountPct = offerDiscountPct(pkg);

  const descText = richTextToPlain(get(service, "description"));
  const conditionText = richTextToPlain(get(campaign, "condition"));

  const fmtDate = (d: any) =>
    d && dayjs(d).isValid() ? formatDate(d, "DD/MM/YYYY") : "";
  const startD = fmtDate(get(campaign, "startDate"));
  const endD = fmtDate(get(campaign, "endDate"));
  const validity = startD && endD ? `${startD} - ${endD}` : "";

  return (
    <Modal
      rootClassName="lm-sheet"
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={640}
      style={{ maxWidth: "92vw" }}
      styles={{
        mask: { backgroundColor: "rgba(0, 0, 0, 0.72)", backdropFilter: "blur(4px)" },
        content: { borderRadius: 20, padding: "clamp(16px, 4vw, 28px)" },
        header: { marginBottom: 16 },
      }}
      title={
        <span className="text-xl md:text-2xl font-semibold">
          {get(pkg, "name")}
        </span>
      }
    >
      <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto pr-1">
        {/* ===== Khối tóm tắt: thumbnail nhỏ + shop ===== */}
        <div className="flex gap-4 items-start">
          {cover && (
            <div className="relative flex-shrink-0 w-[128px] md:w-[168px]">
              <Image
                src={cover}
                alt={coverAlt}
                wrapClassName="relative w-full aspect-[4/3] rounded-xl object-cover overflow-hidden bg-gray-100"
              />
              {discountPct > 0 && (
                <span className="absolute left-2 top-2 rounded-md bg-red-500 px-2 py-0.5 text-xs font-bold text-white shadow">
                  -{discountPct}%
                </span>
              )}
            </div>
          )}
          <div className="min-w-0 flex flex-col gap-1.5">
            {/* Tên + logo shop bấm được → trang chi tiết shop (mở tab mới để không mất modal ưu đãi) */}
            {(() => {
              const shopSlug = get(shop, "slug");
              const inner = (
                <>
                  {avatar && (
                    <Image
                      src={avatar}
                      alt={get(shop, "name") || ""}
                      wrapClassName="w-8 h-8 rounded-md object-cover flex-shrink-0"
                    />
                  )}
                  <span className="font-semibold text-black01 truncate">
                    {get(shop, "name")}
                  </span>
                </>
              );
              return shopSlug ? (
                <Link
                  href={`/${shopSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex w-fit max-w-full items-center gap-2 text-black01 hover:text-primary transition-colors"
                  title={t("offerDetail.viewShop")}
                >
                  {inner}
                  <ChevronRight
                    size={16}
                    className="flex-shrink-0 text-gray-400 group-hover:text-primary"
                  />
                </Link>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">{inner}</div>
              );
            })()}
            {rating > 0 && (
              <span className="inline-flex w-fit items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5">
                <Star size={13} className="fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-bold text-primary">
                  {ratingText}
                </span>
                <span className="text-xs text-gray-500">
                  /10 · {t("offerDetail.rating")}
                </span>
              </span>
            )}
            {get(shop, "displayedAddress") && (
              <p className="text-xs text-gray-500 line-clamp-2 !mb-0">
                {get(shop, "displayedAddress")}
              </p>
            )}
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {categories.map((c, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs"
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Giá */}
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500 !mb-1">
            {t("offerDetail.offerPrice")}
          </p>
          <div className="flex items-baseline gap-3">
            <p className="text-primary font-bold text-2xl !mb-0">
              {formatPrice(offerPrice)}
            </p>
            {originalPrice && originalPrice > offerPrice ? (
              <p className="text-gray-400 line-through text-sm !mb-0">
                {formatPrice(originalPrice)}
              </p>
            ) : null}
            {discountPct > 0 && (
              <span className="ml-auto self-center px-2 py-1 rounded-md bg-red-50 text-red-500 text-xs font-semibold">
                -{discountPct}% {t("offerDetail.discount")}
              </span>
            )}
          </div>
        </div>

        {/* Thời lượng */}
        {duration ? (
          <p className="text-[15px] text-gray-600">
            <span className="font-medium">{t("offerDetail.duration")}: </span>
            {duration} {t("offerDetail.minutes")}
          </p>
        ) : null}

        {/* Mô tả dịch vụ */}
        {descText && (
          <div>
            <p className="font-semibold text-base mb-1.5">
              {t("offerDetail.description")}
            </p>
            <p className="text-[15px] leading-relaxed text-gray-600 whitespace-pre-line">
              {descText}
            </p>
          </div>
        )}

        {/* Điều kiện */}
        {conditionText && (
          <div>
            <p className="font-semibold text-base mb-1.5">
              {t("offerDetail.condition")}
            </p>
            <p className="text-[15px] leading-relaxed text-gray-600 whitespace-pre-line">
              {conditionText}
            </p>
          </div>
        )}

        {/* Thời gian áp dụng */}
        {validity && (
          <p className="text-xs text-gray-500">
            {t("offerDetail.validity")}: {validity}
          </p>
        )}
      </div>

      {/* CTA Đặt lịch */}
      <div className="pt-4">
        <button
          type="button"
          onClick={onBook}
          className="w-full px-4 py-3 rounded-full bg-primary text-white text-base font-semibold hover:opacity-90 transition-opacity"
        >
          {t("offerDetail.bookNow")}
        </button>
      </div>
    </Modal>
  );
};

export default OfferDetailModal;
